import {
  CustomFormSubmissionEvent,
  FormFieldError,
  FormFieldValidationResponse,
  ValidateFormFieldsEvent,
} from "lib/types";

import BaseController from "confinus_ui/base_controller";
import FormFieldController from "confinus_ui/forms/form_field_controller";
import { localeMessage } from "lib/i18n";

import {
  ConfinusUI__Forms__ButtonRadioGroupControllerIdentifier,
  ConfinusUI__Forms__CheckboxControllerIdentifier,
  ConfinusUI__Forms__DateCalendarControllerIdentifier,
  ConfinusUI__Forms__ExpandingCheckboxCollectionControllerIdentifier,
  ConfinusUI__Forms__FileUploadControllerIdentifier,
  ConfinusUI__Forms__FormControllerIdentifier,
  ConfinusUI__Forms__NumberFieldControllerIdentifier,
  ConfinusUI__Forms__RadioControllerIdentifier,
  ConfinusUI__Forms__SelectControllerIdentifier,
  ConfinusUI__Forms__SwitchControllerIdentifier,
  ConfinusUI__Forms__TextAreaControllerIdentifier,
  ConfinusUI__Forms__TextFieldControllerIdentifier,
} from "controllers/identifiers";
import CheckboxController from "confinus_ui/forms/checkbox_controller";

export default class FormController extends BaseController {
  public static outlets = [
    ConfinusUI__Forms__ButtonRadioGroupControllerIdentifier,
    ConfinusUI__Forms__CheckboxControllerIdentifier,
    ConfinusUI__Forms__DateCalendarControllerIdentifier,
    ConfinusUI__Forms__ExpandingCheckboxCollectionControllerIdentifier,
    ConfinusUI__Forms__FileUploadControllerIdentifier,
    ConfinusUI__Forms__FormControllerIdentifier,
    ConfinusUI__Forms__NumberFieldControllerIdentifier,
    ConfinusUI__Forms__RadioControllerIdentifier,
    ConfinusUI__Forms__SelectControllerIdentifier,
    ConfinusUI__Forms__SwitchControllerIdentifier,
    ConfinusUI__Forms__TextAreaControllerIdentifier,
    ConfinusUI__Forms__TextFieldControllerIdentifier,
  ];
  declare private readonly confinusUiFormsButtonRadioGroupOutlets: FormFieldController[];
  declare private readonly confinusUiFormsCheckboxOutlets: CheckboxController[];
  declare private readonly confinusUiFormsDateCalendarOutlets: FormFieldController[];
  declare private readonly confinusUiFormsExpandingCheckboxCollectionOutlets: FormFieldController[];
  declare private readonly confinusUiFormsFileUploadOutlets: FormFieldController[];
  declare private readonly confinusUiFormsNumberFieldOutlets: FormFieldController[];
  declare private readonly confinusUiFormsRadioOutlets: FormFieldController[];
  declare private readonly confinusUiFormsSelectOutlets: FormFieldController[];
  declare private readonly confinusUiFormsSwitchOutlets: FormFieldController[];
  declare private readonly confinusUiFormsTextAreaOutlets: FormFieldController[];
  declare private readonly confinusUiFormsTextFieldOutlets: FormFieldController[];

  public connect() {
    this.element.setAttribute("novalidate", "true");
    super.connect();
  }

  public disconnect() {
    this.element.removeAttribute("novalidate");
    super.disconnect();
  }

  public handleSubmitEvent(evt: Event) {
    // Only handle submit events that originated from this form
    // This prevents validation from running when other forms on the page submit

    // We check if the submitter element is actually contained within THIS form
    // controller's element to ensure we only handle our own form's events.
    // This is a defensive measure against event bubbling and any edge cases.

    const submitEvent = evt as SubmitEvent;

    // Check if the submitter (button) is within our form
    if (submitEvent.submitter) {
      if (!this.element.contains(submitEvent.submitter)) {
        // The submit button is not in our form, skip validation
        return;
      }
    } else {
      // Fallback: check if the event target is within our form
      const target = evt.target as HTMLElement;
      if (!this.element.contains(target) && target !== this.element) {
        return;
      }
    }

    const hasInvalidFields = this.performValidation();
    if (hasInvalidFields) {
      console.debug("Form has invalid fields, will not submit.");
      // At least one field was invalid.
      // Prevent form submission.
      evt.preventDefault();

      // Ensure that the event doesn't propagate.
      // Without this, if data-disable-with is set on the submit button,
      // the button will be disabled even if the form is invalid.
      evt.stopPropagation();

      // Ensure any other submission handlers on this form element are skipped
      evt.stopImmediatePropagation();
    }
  }

  public handleCustomSubmitEvent(evt: Event) {
    // This event is meant for our eyes only.
    evt.stopPropagation();

    const {
      detail: { onSubmissionPrevented },
    } = evt as CustomFormSubmissionEvent;

    const hasInvalidFields = this.performValidation();
    if (hasInvalidFields) {
      onSubmissionPrevented();
    } else {
      this.formElement.submit();
    }
  }

  public handleValidateFieldsEvent(evt: Event) {
    // This event is meant for our eyes only.
    evt.stopPropagation();

    const {
      detail: { onValidated },
    } = evt as ValidateFormFieldsEvent;

    const hasInvalidFields = this.performValidation();
    onValidated(!hasInvalidFields);
  }

  private get formElement(): HTMLFormElement {
    return this.element as HTMLFormElement;
  }

  private get formFieldControllers(): FormFieldController[] {
    // collect all outlets of field types
    return [
      ...this.confinusUiFormsButtonRadioGroupOutlets,
      ...this.confinusUiFormsCheckboxOutlets,
      ...this.confinusUiFormsDateCalendarOutlets,
      ...this.confinusUiFormsExpandingCheckboxCollectionOutlets,
      ...this.confinusUiFormsFileUploadOutlets,
      ...this.confinusUiFormsNumberFieldOutlets,
      ...this.confinusUiFormsRadioOutlets,
      ...this.confinusUiFormsSelectOutlets,
      ...this.confinusUiFormsSwitchOutlets,
      ...this.confinusUiFormsTextAreaOutlets,
      ...this.confinusUiFormsTextFieldOutlets,
    ];
  }

  private performValidation(): boolean {
    console.debug("Perform validation of form...");
    // Validate all fields.
    const { invalidFields, errorMessages } = this.harvestInvalids();

    const hasInvalidFields = invalidFields.length > 0;

    // Issue an event to say that the entire form was validated.
    // This allows higher controllers the opportunity to display
    // an overall summary of the fields that are in error.
    this.emitValidationEvent({
      errors: errorMessages,
      valid: !hasInvalidFields,
    });

    if (hasInvalidFields) {
      console.warn(
        "FormController: The form has invalid fields.",
        errorMessages,
        invalidFields,
      );
      // At least one field was invalid.
      // Focus the first field that is in error.
      invalidFields[0].focusControl();
    }

    return hasInvalidFields;
  }

  private harvestInvalids() {
    let invalidFields: FormFieldController[] = [];
    let errorMessages: FormFieldError[] = [];

    this.formFieldControllers.forEach((fc) => {
      if (fc.disabled) {
        // Don't validate any disabled fields.
        return;
      }
      const { valid, errors } = fc.validate();
      console.debug(
        `FormController: Validating field ${fc.name} (${fc.element})`,
        { valid, errors },
      );
      if (!valid) {
        invalidFields = [...invalidFields, fc];
        errorMessages = [...errorMessages, ...errors];
      }
    });

    return {
      errorMessages,
      invalidFields,
    };
  }

  private emitValidationEvent({ errors, valid }: FormFieldValidationResponse) {
    this.dispatch("validated", {
      bubbles: true,
      cancelable: false,
      detail: {
        errors,
        preamble: localeMessage("form_invalid_preamble"),
        valid,
      },
    });
  }
}
